<template>
  <div
    ref="tableRect"
    class="files-table-wrapper"
  >
    <div class="default-table position-relative">
      <div>
        <div
          class="d-flex justify-content-between align-items-start align-items-md-center p-2 flex-column flex-md-row"
        >
          <directory-breadcrumbs
            :active-key="activeKey"
            :parents="parents"
            :bucket-name="bucketName"
            style="margin-right: 5px;"
            @backToParent="breadCrumbAction"
            @goToManualPath="goToManualPath"
          />

          <div
            class="d-flex align-items-center text-secondary cursor-pointer"
            @click="$emit('refresh')"
          >
            <span>Refresh</span>
            <GjIcon
              class="left-space"
              name="RefreshAlt"
            />
          </div>
        </div>
        <b-table-simple
          responsive
          table-class="mt-0"
          selectable
          class="loaded"
          tbody-tr-class="files-table"
        >
          <b-thead>
            <b-tr>
              <b-th class="table-select-col">
                <select-button
                  :key="selectedRows.length"
                  class="cursor-pointer"
                  :selected="selectedAll"
                  @click="selectAll"
                />
              </b-th>
              <b-th class="dir-table-primary-col">
                Name
              </b-th>
              <b-th class="dir-table-default-col d-none d-lg-table-cell">
                size
              </b-th>
              <b-th class="dir-table-default-cols d-none d-lg-table-cell">
                LAST MODIFIED
              </b-th>

              <b-th class="file-actions" />
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-if="parents"
              class="files-table cursor-pointer"
            >
              <b-td @click="backToParent" />
              <b-td
                colspan="4"
                @click="backToParent"
              >
                <GjIcon name="ArrowUpAlt" />..
              </b-td>
            </b-tr>

            <template v-for="(item, index) in requestFiles">
              <b-tr
                v-if="
                  item.data.isUploading &&
                    item.data.bucketName === bucketName &&
                    item.data.uploadPath === currentUploadPath
                "
                :key="`upload${item.data.isUploading}${index}`"
              >
                <td colspan="5">
                  <upload-progress
                    :index="index"
                    :files="item.uploadData.files.length"
                    @cancel="$emit('cancelUpload', index)"
                  />
                </td>
              </b-tr>
            </template>
            <transition name="dropdown-tr">
              <b-tr
                v-if="newFolderRow"
                v-clickoutside="cancelCreateFolder"
                variant="blue"
              >
                <b-td />
                <b-td class="dir-table-primary-col">
                  <div class="d-flex align-items-center">
                    <directory-field-icon :item="newFolder" />
                    <validation-observer
                      v-slot="{ valid }"
                      ref="createFolder"
                      tag="div"
                      class="d-flex align-items-center ml-1"
                      style="max-width:95%"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="FolderName"
                        :rules="newFolderRules"
                        tag="div"
                        :style="
                          newFolder.key.length > 22
                            ? `width:${newFolder.key.length + 2}ch`
                            : 'width:24ch'
                        "
                      >
                        <b-input
                          ref="newFolderInput"
                          v-model="newFolder.key"
                          class="mb-0"
                          type="text"
                          size="sm"
                          :disabled="addingObject"
                          placeholder="New Folder"
                          :state="errors.length === 0"
                          @keyup.enter="createFolder(valid)"
                          @keyup.esc="cancelCreateFolder"
                        />
                      </validation-provider>
                      <template v-if="!addingObject">
                        <a
                          class="text-white bg-primary rounded-circle rename-button d-flex justify-content-center align-items-center"
                          @click="createFolder(valid)"
                        >
                          <GjIcon
                            name="Check"
                            size="18"
                          />
                        </a>
                        <a
                          class="text-secondary rename-button"
                          @click="cancelCreateFolder"
                        >
                          <GjIcon
                            name="Close"
                            size="22"
                          />
                        </a>
                      </template>
                      <div v-else>
                        <b-spinner
                          class="ml-1"
                          small
                          variant="primary"
                        />
                      </div>
                    </validation-observer>
                  </div>
                </b-td>
                <td />
                <td />
                <td />
              </b-tr>
            </transition>
            <b-tr v-if="initialFetching && gettingObject">
              <b-td
                colspan="5"
                class="text-center"
              >
                <b-spinner
                  variant="primary"
                  small
                />
              </b-td>
            </b-tr>
            <template v-else>
              <b-tr
                v-for="item in content"
                :key="item.key"
                class="files-table"
                :variant="getVariant(item)"
              >
                <b-td class="dir-table-select-col">
                  <select-button
                    v-if="!isRenaming(item.key)"
                    :key="selectedRows.length"
                    class="cursor-pointer"
                    :selected="isRowSelected(item)"
                    @click="selectRow(item)"
                  />
                </b-td>
                <b-td class="dir-table-primary-col">
                  <div class="d-flex">
                    <div
                      class="d-flex align-items-center overflow-visible position-relative"
                    >
                      <directory-field-icon
                        :key="content.length"
                        :item="item"
                        :to-refetch="toRefetch"
                        @removeToRefetch="removeToRefetch"
                        @open="open(item)"
                      />
                    </div>
                    <div
                      v-if="!isRenaming(item.key)"
                      class="d-flex align-items-center text-truncate"
                    >
                      <a
                        class="mx-1 object-name text-truncate"
                        @click.stop="open(item)"
                      >
                        {{ formatObjectName(item.key) }}
                      </a>
                      <b-spinner
                        v-if="gettingFile === item.key"
                        small
                      />
                    </div>
                    <validation-observer
                      v-else
                      v-slot="{ valid }"
                      ref="name"
                      v-clickoutside="cancelRename"
                      tag="div"
                      class="d-flex align-items-center ml-1"
                      style="max-width:95%"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Name"
                        :rules="findRules(file)"
                        tag="div"
                        :style="
                          renaming.newName.length > 22
                            ? `width:${renaming.newName.length + 2}ch`
                            : 'width:24ch'
                        "
                      >
                        <b-input
                          ref="renameInput"
                          v-model="renaming.newName"
                          trim
                          class="mb-0"
                          type="text"
                          size="sm"
                          :state="errors.length === 0"
                          placeholder="New name"
                          @keyup.enter="rename(valid)"
                          @keyup.esc="cancelRename"
                        />
                      </validation-provider>
                      <div
                        v-if="updatingObject"
                        class="ml-1"
                      >
                        <b-spinner small />
                      </div>
                      <template v-else>
                        <a
                          class="text-white bg-primary rounded-circle rename-button d-flex justify-content-center align-items-center"
                          @click.stop="rename(valid)"
                        >
                          <GjIcon
                            name="Check"
                            size="18"
                          />
                        </a>
                        <a
                          class="text-secondary rename-button"
                          @click.stop="cancelRename"
                        >
                          <GjIcon
                            name="Close"
                            size="22"
                          />
                        </a>
                      </template>
                    </validation-observer>
                    <div
                      v-if="!fileIsAvailable(item) && !isRenaming(item.key)"
                      class="d-flex align-items-center ml-1 text-secondary text-sm"
                    >
                      <span class="mr-1">
                        {{ getEvent(item) }}
                      </span>
                      <b-spinner small />
                    </div>
                  </div>
                </b-td>
                <b-td class="dir-table-default-col d-none d-lg-table-cell">
                  <div v-if="!item.isFolder">
                    {{ formatBytes(item.size) }}
                  </div>
                </b-td>
                <b-td class="dir-table-default-col d-none d-lg-table-cell">
                  {{
                    !item.isFolder
                      ? formatOffsetDateTime(item.lastModified)
                      : ''
                  }}
                </b-td>
                <b-td class="file-actions py-0">
                  <a @click="fileSettings(item, $event)">
                    <GjIcon name="Moreoptions" />
                  </a>
                </b-td>
              </b-tr>
            </template>
            <b-tr
              v-if="
                !content.length &&
                  !newFolderRow &&
                  !initialFetching &&
                  !gettingObject
              "
            >
              <b-td colspan="5">
                <no-data
                  no-data-text="Directory is empty"
                  create-text="+ Click here to upload files"
                  icon="Files"
                  @action="noDataAction"
                />
              </b-td>
            </b-tr>
            <tr
              v-if="hasMore && !gettingObject"
              class="files-table cursor-pointer"
            >
              <template v-if="loadingMore && refreshingObjects">
                <b-td />
                <b-td colspan="4">
                  <div class="d-flex align-items-center">
                    <span class="mr-1">
                      Loading...
                    </span>
                    <b-spinner small />
                  </div>
                </b-td>
              </template>
              <template v-else>
                <b-td @click="loadMore" />
                <b-td
                  colspan="4"
                  @click="loadMore"
                >
                  Load more...
                </b-td>
              </template>
            </tr>
          </b-tbody>
        </b-table-simple>
        <div class="default-pagination d-flex align-items-center justify-content-between">
          <div class="results px-2">
            {{ content.length }} Items
          </div>
          <span
            class="text-secondary mr-3"
          >{{
            itemsNumber.length !== 1
              ? `${itemsNumber} items`
              : `${itemsNumber} item`
          }}
            / {{ itemsSize }} total</span>
        </div>
      </div>
    </div>
    <div v-if="showFieldActions && !isRenaming(file.key)">
      <directory-field-actions
        :item="file"
        :is-folder="file.isFolder"
        :object-name="formatObjectName(file.key)"
        :rect="rect"
        :table-rect-top="tableRectTop"
        :table-rect-left="tableRectLeft"
        :account-id="accountId"
        @rename.stop="initRename"
        @remove="remove(file)"
        @move="move(file)"
        @copy="copy(file)"
        @quickShare="quickShare(file)"
        @copied="copied"
        @updated="addToRefetch"
        @close="showFieldActions = false"
      />
    </div>
    <b-modal
      v-model="quickShareModal"
      title="Quick share"
      ok-variant="outline-primary"
      ok-title="Close"
      body-class="p-0 position-relative"
      ok-only
      hide-footer
      centered
      @hidden="() => {}"
      @ok.prevent="() => {}"
    >
      <quick-share
        :quick-share-item="quickShareItem"
        :account-id="accountId"
      />
    </b-modal>
  </div>
</template>

<script>
import StorageHelpers from '@/mixins/storage-helpers'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, alphaDash } from '@validations'

const newFolder = {
  key: '',
  newFolder: true,
  isFolder: true,
  _rowVariant: 'blue',
}

export default {
  name: 'DirectoryContent',
  components: {
    DirectoryFieldActions: () => import('./helpers/DirectoryFieldActions.vue'),
    DirectoryFieldIcon: () => import('./helpers/DirectoryFieldIcon.vue'),
    DirectoryBreadcrumbs: () => import('./helpers/DirectoryBreadcrumbs.vue'),
    UploadProgress: () => import('@/components/info/UploadProgress.vue'),
    QuickShare: () => import('./helpers/QuickShare.vue'),
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [StorageHelpers],
  props: {
    activeKey: {
      type: String,
      default: '',
    },
    selected: {
      type: Array,
      default: () => [],
      required: true,
    },
    newFolderRow: {
      type: Boolean,
      default: false,
    },
    parents: {
      type: Array,
      default: null,
    },
    keyToFocus: {
      type: String,
      default: null,
    },
    initialFetching: {
      type: Boolean,
      default: false,
    },
    gettingFile: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      required,
      alphaDash,
      fields: [
        {
          key: 'selected',
          label: '',
          tdClass: 'table-select-col',
          thClass: 'table-select-col',
        },
        {
          key: 'key',
          sortable: true,
          tdClass: 'dir-table-primary-col',
          thClass: 'dir-table-primary-col',
        },
        {
          key: 'size',
          sortable: true,
          tdClass: 'dir-table-default-col',
          thClass: 'dir-table-default-col',
        },
        {
          key: 'lastModified',
          sortable: true,
          tdClass: 'dir-table-default-col',
          thClass: 'dir-table-default-col',
        },
        {
          key: 'actions',
          label: '',
          tdClass: 'py-0 file-actions',
          thClass: 'py-0 file-actions',
        },
      ],
      renaming: {
        key: null,
        newName: '',
        errorMessage: null,
      },
      newFolder: {
        ...newFolder,
      },
      selectedRows: [],
      quickShareModal: false,
      quickShareItem: null,
      moveObjectItem: null,
      toRefetch: [],
      isUploading: false,
      showFieldActions: false,
      file: null,
      rect: null,
      tableRect: null,
      accountId: 0,
    }
  },
  computed: {
    newFolderRules() {
      const length = this.activeKey ? this.activeKey.length : 0
      return `required|max:${250 - length}|validFolderName`
    },
    bucketName() {
      return this.$route.params.id
    },
    itemsNumber() {
      return this.$store.state.accountObjects.objects.length
    },
    itemsSize() {
      let size = 0
      this.$store.state.accountObjects.objects.forEach(item => {
        size += item.size
      })
      return this.formatBytes(size)
    },
    addingObject() {
      return this.$store.state.accountObjects.addingObject
    },
    updatingObject() {
      return this.$store.state.accountObjects.updatingObject
    },
    content() {
      return this.$store.state.accountObjects.objects
    },
    fetchingObjects() {
      return this.$store.state.accountObjects.fetchingObjects
    },
    gettingObject() {
      return this.$store.state.accountObjects.gettingObject
    },
    refreshingObjects() {
      return this.$store.state.accountObjects.refreshingObjects
    },
    hasMore() {
      return this.$store.state.accountObjects.hasMore
    },
    loadingMore() {
      return this.$store.state.accountObjects.loadingMore
    },
    jobStatusOptions() {
      return this.$store.state.accountObjects.jobStatusOptions
    },
    eventTypes() {
      return this.$store.state.accountObjects.eventTypes
    },
    selectedAll() {
      if (this.content.length === 0) return false
      return this.content.length === this.selectedRows.length
    },
    requestFiles() {
      return this.$store.state.uploading.requestFiles
    },
    currentUploadPath() {
      return this.$store.state.uploading.currentUploadPath
    },
    tableRectTop() {
      return this.tableRect?.top
    },
    tableRectLeft() {
      return this.tableRect?.left
    },
  },
  watch: {
    newFolderRow(val) {
      if (val) {
        this.newFolder = { ...newFolder }
        this.selectedRows = []
        this.resetRenamingObj()
        setTimeout(() => {
          if (this.$refs.newFolderInput) {
            this.$refs.newFolderInput.focus()
          }
        }, 400)
      }
    },
    content() {
      this.selectedRows = []
    },
    selectedRows(val) {
      this.$emit('rowSelected', val)
    },
    requestFiles() {
      this.isUploading = this.requestFiles.filter(e => e.data.isUploading).length > 0
    },
  },
  created() {
    this.accountId = this.$route.params.accountId
  },
  methods: {
    addToRefetch(el) {
      this.toRefetch.push(el)
    },
    removeToRefetch(el) {
      this.toRefetch = this.toRefetch.filter(item => item !== el)
    },
    fileIsAvailable(item) {
      return (
        (item.eventType === this.eventTypes.Default
          && item.status === this.jobStatusOptions.Available)
        || !!item.manuallyAdded
      )
    },
    getEvent(item) {
      switch (item.eventType) {
        case this.eventTypes.DeleteFolder:
          return 'Deleting Folder'
        case this.eventTypes.MoveFolder:
          return 'Moving Folder'
        case this.eventTypes.DeleteObject:
          return 'Deleting File'
        case this.eventTypes.MoveObject:
          return 'Moving Object'
        default:
          return ''
      }
    },
    move(item) {
      this.showFieldActions = false
      this.$emit('move', [item])
    },
    copy(item) {
      this.showFieldActions = false
      this.$emit('copy', [item])
    },
    quickShare(item) {
      this.showFieldActions = false
      this.quickShareItem = item
      this.quickShareModal = true
    },
    getVariant(item) {
      if (this.isRowFocused(item)) {
        return 'green'
      }
      if (this.isRowSelected(item)) {
        return 'blue'
      }
      if (this.isRenaming(item.key)) {
        return 'blue'
      }
      return ''
    },
    loadMore() {
      this.$emit('loadMore')
    },
    breadCrumbAction(key) {
      if (key) {
        this.$emit('backToParent', key)
      } else {
        this.$emit('backToParent', null)
      }
    },
    goToManualPath(key) {
      const to = `${key}`
      if (this.isNullOrEmpty(key)) return

      this.$emit('open', {
        isFolder: true,
        key: to,
      })
    },
    noDataAction() {
      this.$emit('uploadFiles')
    },
    createFolder(valid) {
      if (valid) {
        this.$emit('createFolder', this.newFolder.key)
      }
    },
    cancelCreateFolder() {
      this.$emit('cancelCreateFolder')
    },
    rowSelected(selected) {
      if (selected.length) {
        this.cancelCreateFolder()
      }
      this.$emit('rowSelected', selected)
    },
    resetRenamingObj() {
      this.renaming.key = null
      this.renaming.newName = ''
      this.renaming.errormessage = null
    },
    rowClicked() {
      if (this.renaming.id !== null) {
        this.$refs.directoryTable.clearSelected()
        this.resetRenamingObj()
      }
    },
    isRenaming(key) {
      return this.renaming?.key === key
    },
    open(item) {
      this.$emit('open', item)
    },
    backToParent() {
      const tempParents = [...this.parents]
      if (tempParents) {
        if (tempParents.length === 1) {
          this.$emit('backToParent', null)
          return
        }
        if (tempParents.length > 1) {
          this.$emit(
            'backToParent',
            `${tempParents.splice(0, tempParents.length - 1).join('/')}/`,
          )
        }
      }
    },
    rename(valid) {
      if (valid) {
        const newKeyArr = this.keyToArr(this.renaming.key)
        const { isFolder } = this.renaming
        newKeyArr[newKeyArr.length - 1] = this.renaming.newName
        const newKey = `${newKeyArr.join('/')}${isFolder ? '/' : ''}`
        const updateObj = {
          prefixes: {},
          keys: {},
        }
        const objAttr = isFolder ? 'prefixes' : 'keys'

        updateObj[objAttr][this.renaming.key] = newKey
        this.$store
          .dispatch('accountObjects/updateObjectKeys', {
            accountId: this.accountId,
            query: {
              rename: true,
              bucketName: this.bucketName,
              updateObj,
            },
          })
          .then(() => {
            setTimeout(() => {
              this.$store.dispatch('accountObjects/refreshUntilStatusCleared', { accountId: this.accountId })
            }, 1000)

            this.successToast('Renamed file/folder')
            this.cancelRename()
          })
          .catch(() => {
            this.errorToast('Error renaming file/folder')
            this.cancelRename()
          })
      }
    },
    remove(item) {
      this.$emit('remove', [item])
      this.showFieldActions = false
    },
    initRename() {
      const item = this.file
      this.showFieldActions = false
      this.renaming.key = item.key
      this.renaming.isFolder = item.isFolder
      const newName = this.formatObjectName(item.key)
      this.renaming.newName = newName !== '[Empty name]' ? newName : ''
      this.selectedRows = []
      setTimeout(() => {
        if (this.$refs.renameInput) {
          this.$refs.renameInput[0].focus()
        }
      }, 300)
    },
    cancelRename() {
      this.resetRenamingObj()
    },
    selectRow(item) {
      this.cancelCreateFolder()
      if (!this.isRowSelected(item)) {
        this.selectedRows = [...this.selectedRows, item]
      } else {
        this.selectedRows = this.selectedRows.filter(
          row => row.key !== item.key,
        )
      }
    },
    selectAll() {
      if (this.selectedAll) {
        this.selectedRows = []
        return
      }
      this.selectedRows = [...this.content]
    },
    isRowSelected(item) {
      return this.selectedRows.some(row => row.key === item.key)
    },
    isRowFocused(item) {
      return this.keyToFocus === item.key
    },
    fileSettings(item, e) {
      this.tableRect = this.$refs.tableRect.getBoundingClientRect()
      this.rect = e.target.getBoundingClientRect()
      this.file = item
      this.showFieldActions = true
      e.stopPropagation()
    },
    copied() {
      this.showFieldActions = false
      this.copySuccess()
    },
    findRules(file) {
      return file.isFolder ? this.newFolderRules : 'required|filename'
    },
  },
}
</script>

<style scoped>
.left-space {
  margin-left:4px;
}
a.rename-button {
  margin-left: 4px;
  box-shadow: none !important;
  padding: 1px;
}
</style>
